import { useFormContext, useFormState } from 'react-hook-form';
import {
  SETTINGS_HAS_UNSAVED_CHANGES_MODAL,
  SETTINGS_SAVE_CHANGES_MODAL,
} from 'modules/workflows/constants/modals';
import { TILE_METRICS_STUB } from 'modules/workflows/constants/nodeAnalytics';
import { openModal } from 'modules/workflows/ducks';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsJourneyGoalsWithExitsFeatureFlagOn,
} from 'core/userContext';
import { useSaveJourneySettings } from './hooks/useSaveJourneySettings';
import { SettingsTab } from './SettingsTakeover.constants';
import { getDefaultTileMetrics } from './Metrics/Metrics.utils';
import { useRuleBuilder } from '../RuleBuilder/hooks/useRuleBuilder';
import { useSaveChangesModalBody } from './hooks/useSaveChangesModalBody';
import { useTabValidation } from './hooks/useTabValidation';

export const useSaveSettings = (onClose: () => void) => {
  const dispatch = useDispatch();
  const { reset, getValues, trigger, setValue } = useFormContext();
  const { dirtyFields } = useFormState();
  const setTabValue = (tab: SettingsTab) => setValue('currentTab', tab);
  const formValues = getValues();

  const { runValidation } = useTabValidation(formValues?.currentTab);

  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );

  const modalBody = useSaveChangesModalBody(formValues, dirtyFields);

  const { checkForValidationErrors } = useRuleBuilder();

  const saveFormValues = async (saveJourneySettingsFn: () => void) => {
    if (isJourneyGoalsEnabled) {
      // if we have the feature flag on and we have validation errors within the exit rules, we need to
      if (isJourneyGoalsWithExitsFeatureFlagOn) {
        const validForm = await trigger();
        const exitRulesValidation = checkForValidationErrors();

        if (!validForm || exitRulesValidation) {
          runValidation();
          return;
        }
      }
      dispatch(
        openModal(SETTINGS_SAVE_CHANGES_MODAL, {
          onSave: saveJourneySettingsFn,
          onDiscard: onClose,
          modalBody,
        }),
      );
    } else {
      saveJourneySettingsFn();
    }
  };

  const { saveJourneySettings, isSaving } = useSaveJourneySettings({
    onSetTabValue: setTabValue,
  });

  const { saveJourneySettings: saveJourneySettingsAndExit, isSaving: isSavingAndExiting } =
    useSaveJourneySettings({
      onSetTabValue: setTabValue,
    });

  return {
    handleSave: async () =>
      saveFormValues(() => saveJourneySettings(() => reset(getValues(), { keepDirty: false }))),
    handleSaveAndExit: async () => saveFormValues(() => saveJourneySettingsAndExit(onClose)),
    isSaving: isSaving || isSavingAndExiting,
  };
};

export const useCloseSettings = (onClose: () => void) => {
  const dispatch = useDispatch();
  const { trigger, setValue } = useFormContext();
  const { isDirty } = useFormState();
  const setTabValue = (tab: SettingsTab) => setValue('currentTab', tab);

  const { saveJourneySettings } = useSaveJourneySettings({
    onSetTabValue: setTabValue,
  });
  const { checkForValidationErrors } = useRuleBuilder();
  return async () => {
    const validForm = await trigger();
    if (isDirty) {
      dispatch(
        openModal(SETTINGS_HAS_UNSAVED_CHANGES_MODAL, {
          onSave: () => saveJourneySettings(onClose),
          onDiscard: onClose,
          hasValidationErrors: checkForValidationErrors() || !validForm,
        }),
      );
    } else {
      onClose();
    }
  };
};

export const useSwitchTab = () => {
  const dispatch = useDispatch();
  const { setRules, checkForValidationErrors } = useRuleBuilder();
  const { getValues, trigger, reset, setValue } = useFormContext();
  const { dirtyFields, defaultValues } = useFormState();

  const setTabValue = (tab: SettingsTab) => setValue('currentTab', tab);

  const { saveJourneySettings, isSaving } = useSaveJourneySettings({
    onSetTabValue: setTabValue,
  });

  const { currentTab } = getValues();

  const switchTab = async (tab: SettingsTab) => {
    const validForm = await trigger();
    // check if dirty and if so show unsaved changes modal.
    const tabDirty =
      currentTab === SettingsTab.ConversionsAndMetrics
        ? dirtyFields.conversions || dirtyFields.metrics
        : dirtyFields?.[`${currentTab}`];

    if (tabDirty) {
      const finishedFn = () => {
        reset(getValues(), { keepDirty: false });
        setTabValue(tab);
      };

      dispatch(
        openModal(SETTINGS_HAS_UNSAVED_CHANGES_MODAL, {
          onSave: () => saveJourneySettings(finishedFn),
          onDiscard: () => {
            if (currentTab === SettingsTab.ExitRules) {
              setRules(defaultValues?.['exit-rules'] || []);
            }
            reset(defaultValues, { keepDirty: false });
            setTabValue(tab);
          },
          hasValidationErrors: checkForValidationErrors() || !validForm,
        }),
      );
    } else {
      setTabValue(tab);
    }
  };
  return { switchTab, isSaving };
};

export const useResetMetrics = () => {
  const { reset, getValues, setValue } = useFormContext();
  const { dirtyFields } = useFormState();
  const { saveJourneySettings, isSaving } = useSaveJourneySettings({});

  const resetMetrics = () => {
    const isConversionDirty = dirtyFields.conversions;
    /* 
          This is a hack for resetting the metrics field to default values.  When you set these values, the controlled input does not 
          rerender until we do a reset.
        */
    const finalFn = () => {
      setValue(SettingsTab.Metrics, getDefaultTileMetrics(TILE_METRICS_STUB), {
        shouldDirty: !!isConversionDirty,
      });
      reset(
        {
          ...getValues(),
          [SettingsTab.Metrics]: getDefaultTileMetrics(TILE_METRICS_STUB),
        },
        {
          keepDirtyValues: !!isConversionDirty,
          keepDirty: !!isConversionDirty,
        },
      );
    };
    saveJourneySettings(finalFn);
  };
  return { resetMetrics, isSaving };
};
